import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { colors } from '../styles/styles';
import useAllThoughts from '../hooks/useAllThoughts';
import { buildThoughtIndex } from '../utils/fuzzySearch';

// Search: a direct, one-tap keyword search over ALL of your thoughts — the same
// fields the Library's "Your Thoughts" box covers (body/theme/motif/tags), but
// typo-tolerant and relevance-ranked (Fuse). Distinct from Reflect, which is the
// semantic "search by feeling" page.
const formatDate = (ts) => {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

const SearchView = () => {
  const navigate = useNavigate();
  const { thoughts, loading } = useAllThoughts();
  const [term, setTerm] = useState('');

  // Build the Fuse index once per corpus, not per keystroke.
  const index = useMemo(() => buildThoughtIndex(thoughts), [thoughts]);
  const query = term.trim();
  const results = useMemo(
    () => (query ? index.search(query).map((r) => r.item) : []),
    [index, query]
  );

  return (
    <div
      className="page-content"
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        padding: '80px 16px 40px',
        maxWidth: '720px',
        margin: '0 auto',
        backgroundColor: colors.background,
        color: colors.textPrimary,
      }}
    >
      <input
        type="text"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder="Search your thoughts"
        autoFocus
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '14px 16px',
          borderRadius: '12px',
          backgroundColor: colors.surfaceElevated,
          color: colors.textPrimary,
          border: `1px solid ${colors.divider}`,
          fontSize: '16px', // >=16px avoids iOS Safari zoom-on-focus
          lineHeight: 1.5,
          fontFamily: 'inherit',
          outline: 'none',
        }}
      />

      <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {!query && !loading && (
          <p style={{ color: colors.textTertiary, fontSize: '14px' }}>
            Search across every thought — body, theme, motif, and tags. Typos are okay.
          </p>
        )}

        {query && loading && (
          <p style={{ color: colors.textTertiary, fontSize: '14px' }}>
            Loading your thoughts…
          </p>
        )}

        {/* Only claim "no matches" once the corpus has actually loaded —
            otherwise a fast typer sees a false empty-state over an empty index. */}
        {query && !loading && results.length === 0 && (
          <p style={{ color: colors.textSecondary, fontSize: '14px' }}>
            No matching thoughts found.
          </p>
        )}

        {results.map((t) => (
          <div
            key={t.id}
            onClick={() => t.id && navigate(`/thought/${t.id}`)}
            className="ios-button"
            style={{
              padding: '14px 16px',
              borderRadius: '12px',
              backgroundColor: colors.surfaceElevated,
              border: `1px solid ${colors.divider}`,
              cursor: t.id ? 'pointer' : 'default',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
              <span style={{ color: colors.textTertiary, fontSize: '12px' }}>{formatDate(t.timestamp)}</span>
              {t.theme ? (
                <span style={{ color: colors.textTertiary, fontSize: '12px' }}>{t.theme}</span>
              ) : null}
            </div>
            <div style={{ fontSize: '15px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{t.thought}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SearchView;
